import { type ChartArea, type FontSpec, type RadialLinearScale } from 'chart.js';
import { toFont } from 'chart.js/helpers';

// Chart.js shrinks the spider by at most half its radius, then slides the centre
// toward whichever side sticks out more. A label wider than the room left at that
// shifted centre is painted past the canvas. Wrap only those labels.
const RADAR_POINT_LABEL_PADDING = 5;
const MAXIMUM_LAYOUT_ROUNDS = 4;
const FIT_TOLERANCE_PIXELS = 0.5;

type WrappedLabel = string | string[];

type PointLabelGeometry = {
	radians: number;
	degrees: number;
	sine: number;
	cosine: number;
	padding: number;
};

type LabelSize = {
	width: number;
	height: number;
};

type CachedLayout = {
	key: string;
	lines: WrappedLabel[];
};

const labelLayouts = new WeakMap<RadialLinearScale, CachedLayout>();

function pointLabelPadding(scale: RadialLinearScale): number {
	const paddingOption = scale.options.pointLabels.padding;
	return typeof paddingOption === 'number' ? paddingOption : RADAR_POINT_LABEL_PADDING;
}

function pointLabelFont(scale: RadialLinearScale) {
	const configured = scale.options.pointLabels.font;
	if (typeof configured !== 'object' || configured === null) {
		return toFont({});
	}
	return toFont(configured as Partial<FontSpec>);
}

function pointLabelGeometry(scale: RadialLinearScale): PointLabelGeometry[] {
	// getIndexAngle() divides by _pointLabels.length, which is still the previous
	// layout's labels while this callback is building the new ones.
	const labels = scale.getLabels();
	const count = labels.length || 1;
	const startAngle = ((scale.options.startAngle || 0) * Math.PI) / 180;
	const centered = scale.options.pointLabels.centerPointLabels ? Math.PI / count : 0;
	const fullTurn = Math.PI * 2;
	const padding = pointLabelPadding(scale);

	return labels.map((_, index) => {
		const radians =
			((((index * fullTurn) / count + startAngle + centered) % fullTurn) + fullTurn) % fullTurn;
		const degrees = ((Math.round((radians * 180) / Math.PI) % 360) + 360) % 360;
		return {
			radians,
			degrees,
			sine: Math.sin(radians),
			cosine: Math.cos(radians),
			padding,
		};
	});
}

// Same rule as Chart.js determineLimits: rounded degrees decide whether the label
// hangs off one side of its anchor or is centred on it.
function determineLimits(
	degrees: number,
	position: number,
	size: number,
	minimum: number,
	maximum: number,
): { start: number; end: number } {
	if (degrees === minimum || degrees === maximum) {
		return { start: position - size / 2, end: position + size / 2 };
	}
	if (degrees < minimum || degrees > maximum) {
		return { start: position - size, end: position };
	}
	return { start: position, end: position + size };
}

function overflowToMovement(
	start: number,
	end: number,
	boundStart: number,
	boundEnd: number,
	absoluteComponent: number,
): { towardStart: number; towardEnd: number } {
	// Sine or cosine is zero when the label sits on the vertical or horizontal axis.
	// Dividing that overflow by zero would push the centre to infinity.
	if (absoluteComponent === 0) {
		return { towardStart: 0, towardEnd: 0 };
	}
	if (start < boundStart) {
		return { towardStart: (boundStart - start) / absoluteComponent, towardEnd: 0 };
	}
	if (end > boundEnd) {
		return { towardStart: 0, towardEnd: (end - boundEnd) / absoluteComponent };
	}
	return { towardStart: 0, towardEnd: 0 };
}

// Mirrors RadialLinearScale.setCenterPoint in Chart.js 4.5. The radius shrinks by
// the largest overflow, and by at most half. The centre then slides by half the
// difference between the left and right overflow, toward the shorter side.
function simulateFit(
	scale: RadialLinearScale,
	geometries: PointLabelGeometry[],
	sizes: LabelSize[],
	chartArea: ChartArea,
): { centerX: number; radius: number } {
	let leftMovement = 0;
	let rightMovement = 0;
	let topMovement = 0;
	let bottomMovement = 0;

	for (let index = 0; index < geometries.length; index++) {
		const geometry = geometries[index];
		const size = sizes[index];
		if (!geometry || !size) {
			continue;
		}

		// Tick labels on this chart are hidden, so there is no backdrop inset.
		// Chart.js then puts every anchor at drawingArea + padding.
		const distance = scale.drawingArea + geometry.padding;
		const anchorX = scale.xCenter + geometry.sine * distance;
		const anchorY = scale.yCenter - geometry.cosine * distance;
		const horizontal = determineLimits(geometry.degrees, anchorX, size.width, 0, 180);
		const vertical = determineLimits(geometry.degrees, anchorY, size.height, 90, 270);
		// Alignment uses the rounded degree, but the radius change uses the raw sine and cosine.
		const horizontalMovement = overflowToMovement(
			horizontal.start,
			horizontal.end,
			chartArea.left,
			chartArea.right,
			Math.abs(geometry.sine),
		);
		const verticalMovement = overflowToMovement(
			vertical.start,
			vertical.end,
			chartArea.top,
			chartArea.bottom,
			Math.abs(geometry.cosine),
		);

		leftMovement = Math.max(leftMovement, horizontalMovement.towardStart);
		rightMovement = Math.max(rightMovement, horizontalMovement.towardEnd);
		topMovement = Math.max(topMovement, verticalMovement.towardStart);
		bottomMovement = Math.max(bottomMovement, verticalMovement.towardEnd);
	}

	const shrink = Math.min(
		scale.drawingArea / 2,
		Math.max(leftMovement, rightMovement, topMovement, bottomMovement),
	);

	return {
		centerX: scale.xCenter + Math.floor((leftMovement - rightMovement) / 2),
		radius: scale.drawingArea - shrink,
	};
}

function availableWidth(
	geometry: PointLabelGeometry,
	centerX: number,
	radius: number,
	chartArea: ChartArea,
): number {
	if (geometry.degrees === 0 || geometry.degrees === 180) {
		return Math.max(Math.min(chartArea.right - centerX, centerX - chartArea.left) * 2, 1);
	}

	const inset = radius + geometry.padding;
	if (geometry.degrees < 180) {
		return Math.max(chartArea.right - centerX - geometry.sine * inset, 1);
	}
	return Math.max(centerX - chartArea.left + geometry.sine * inset, 1);
}

function splitWord(word: string, maxWidth: number, measure: (line: string) => number): string[] {
	const lines: string[] = [];
	let current = '';

	for (const character of word) {
		const next = current + character;
		if (current && measure(next) > maxWidth) {
			lines.push(current);
			current = character;
			continue;
		}
		current = next;
	}

	if (current) {
		lines.push(current);
	}

	if (lines.length !== 2) {
		return lines;
	}

	// A greedy break can leave a tiny second line ("ment"). Use the most even split that still fits.
	const characters = [...word];
	let balanced = lines;
	let shortest = Math.min(measure(lines[0] ?? ''), measure(lines[1] ?? ''));
	let prefix = '';
	for (let index = 0; index < characters.length - 1; index++) {
		prefix += characters[index];
		const rest = characters.slice(index + 1).join('');
		const prefixWidth = measure(prefix);
		const restWidth = measure(rest);
		if (prefixWidth > maxWidth || restWidth > maxWidth) {
			continue;
		}
		const line = Math.min(prefixWidth, restWidth);
		if (line > shortest) {
			shortest = line;
			balanced = [prefix, rest];
		}
	}
	return balanced;
}

function wrapLabel(text: string, maxWidth: number, measure: (line: string) => number): string[] {
	const words = text.split(/\s+/).filter((word) => word.length > 0);
	const lines: string[] = [];
	let current = '';

	const pushCurrent = () => {
		if (current) {
			lines.push(current);
			current = '';
		}
	};

	for (const word of words) {
		const next = current ? `${current} ${word}` : word;
		if (measure(next) <= maxWidth) {
			current = next;
			continue;
		}

		pushCurrent();
		if (measure(word) <= maxWidth) {
			current = word;
			continue;
		}

		const parts = splitWord(word, maxWidth, measure);
		lines.push(...parts.slice(0, -1));
		current = parts[parts.length - 1] ?? '';
	}

	pushCurrent();
	return lines;
}

function wrapToWidth(
	label: string,
	maxWidth: number,
	measure: (line: string) => number,
): WrappedLabel {
	if (!label || !Number.isFinite(maxWidth) || measure(label) <= maxWidth) {
		return label;
	}

	const lines = wrapLabel(label, maxWidth, measure);
	if (lines.length <= 1) {
		return lines[0] ?? label;
	}
	return lines;
}

function labelSize(
	lines: WrappedLabel,
	measure: (line: string) => number,
	lineHeight: number,
): LabelSize {
	const rows = Array.isArray(lines) ? lines : [lines];
	let width = 0;
	for (const row of rows) {
		width = Math.max(width, measure(row));
	}
	return { width, height: rows.length * lineHeight };
}

function chartAreaOf(scale: RadialLinearScale): ChartArea | null {
	const chartArea = scale.chart.chartArea;
	if (!chartArea || !(scale.drawingArea > 0)) {
		return null;
	}

	const { left, top, right, bottom } = chartArea;
	const edgesAreFinite = [left, top, right, bottom, scale.xCenter, scale.yCenter].every((value) =>
		Number.isFinite(value),
	);
	return edgesAreFinite ? chartArea : null;
}

function layoutKey(scale: RadialLinearScale, chartArea: ChartArea, font: string, labels: string[]) {
	return [
		scale.drawingArea,
		scale.xCenter,
		scale.yCenter,
		chartArea.left,
		chartArea.top,
		chartArea.right,
		chartArea.bottom,
		font,
		labels.join('\u0000'),
	].join('|');
}

function wrapToFit(
	scale: RadialLinearScale,
	labels: string[],
	geometries: PointLabelGeometry[],
	chartArea: ChartArea,
	measure: (line: string) => number,
	lineHeight: number,
): WrappedLabel[] {
	// Budget as if Chart.js has already applied its maximum shrink and the centre
	// has not moved. Anything wider than that cannot fit. Later rounds use the
	// centre and radius the fit will actually end on, and only tighten.
	let maximumWidths = geometries.map((geometry) =>
		availableWidth(geometry, scale.xCenter, scale.drawingArea / 2, chartArea),
	);
	let lines: WrappedLabel[] = labels;

	for (let round = 0; round < MAXIMUM_LAYOUT_ROUNDS; round++) {
		lines = labels.map((label, index) => wrapToWidth(label, maximumWidths[index] ?? 1, measure));
		const sizes = lines.map((wrapped) => labelSize(wrapped, measure, lineHeight));
		const fitted = simulateFit(scale, geometries, sizes, chartArea);
		if (round === MAXIMUM_LAYOUT_ROUNDS - 1) {
			break;
		}

		const nextWidths = maximumWidths.slice();
		let narrowed = false;
		for (let index = 0; index < labels.length; index++) {
			const geometry = geometries[index];
			const size = sizes[index];
			if (!geometry || !size) {
				continue;
			}

			const room = availableWidth(geometry, fitted.centerX, fitted.radius, chartArea);
			if (size.width <= room + FIT_TOLERANCE_PIXELS) {
				continue;
			}
			if (room < (maximumWidths[index] ?? room)) {
				nextWidths[index] = room;
				narrowed = true;
			}
		}

		if (!narrowed) {
			break;
		}
		maximumWidths = nextWidths;
	}

	return lines;
}

function layoutRadarPointLabels(scale: RadialLinearScale): WrappedLabel[] | null {
	const chartArea = chartAreaOf(scale);
	if (!chartArea) {
		return null;
	}

	const labels = scale.getLabels().map((label) => (label == null ? '' : String(label)));
	const font = pointLabelFont(scale);
	const key = layoutKey(scale, chartArea, font.string, labels);
	const cached = labelLayouts.get(scale);
	if (cached?.key === key) {
		return cached.lines;
	}

	const geometries = pointLabelGeometry(scale);
	const context = scale.ctx;
	context.save();
	context.font = font.string;
	const measure = (line: string) => context.measureText(line).width;

	try {
		const lines = wrapToFit(scale, labels, geometries, chartArea, measure, font.lineHeight);
		labelLayouts.set(scale, { key, lines });
		return lines;
	} finally {
		context.restore();
	}
}

export function wrapRadarPointLabel(
	scale: RadialLinearScale,
	label: string,
	index: number,
): string | string[] {
	const lines = layoutRadarPointLabels(scale);
	if (!lines) {
		return label;
	}
	return lines[index] ?? label;
}
